import { CB_CLIENT } from '@onramp/config/cbClient';
// eslint-disable-next-line import/no-cycle
import { getRouteWithParams } from '@onramp/hooks/useOnRampRouter';
import {
  APP_ID_HEADER,
  CB_CLIENT_HEADER,
  CB_CLIENT_SESSION_ID_HEADER,
  CBPAY_GUEST_SESSION_UUID_HEADER,
  CSRF_TOKEN_HEADER,
  PLATFORM_ATTRIBUTION_HEADER,
  USER_ID_HEADER,
} from '@onramp/shared/headerKeys';
import { secureInitParametersAtom } from '@onramp/state/recoil/atoms/secureInitAtoms';
import { jotaiStore } from '@onramp/state/recoil/utils';
import { flushQueue, getAnalyticsHeaders } from '@cbhq/client-analytics';
import { createFetch, defaultFetchConfig } from '@cbhq/data-layer';
import type { CbDataRequestInit } from '@cbhq/data-layer/network';

import { clientSessionIdStore } from './clientSessionIdStore';
import { getCustomLogger } from './logger';

// Common headers for REST and GQL requests
export const getCommonHeaders = (): Record<string, string> => {
  const userUuid = clientSessionIdStore.getUserUuid();
  const sessionId = clientSessionIdStore.getClientSessionId();
  const csrfToken = clientSessionIdStore.getCsrfToken();
  const appId = clientSessionIdStore.getAppId();
  const appAttribution = clientSessionIdStore.getPlatformAttribution();
  const { sessionUuid } = jotaiStore.get(secureInitParametersAtom) ?? {};

  return {
    ...getAnalyticsHeaders(),
    'Content-Type': 'application/json',
    [CB_CLIENT_HEADER]: CB_CLIENT,
    [CB_CLIENT_SESSION_ID_HEADER]: sessionId,
    ...(appId && { [APP_ID_HEADER]: appId }),
    ...(appAttribution && { [PLATFORM_ATTRIBUTION_HEADER]: appAttribution }),
    ...(userUuid && { [USER_ID_HEADER]: userUuid }),
    ...(csrfToken && { [CSRF_TOKEN_HEADER]: csrfToken }),
    // This sessionUuid is already stored in the secure init cookie, we're sending it in a header specifically to
    // support the API first Apple Pay flow being iframed in Safari. Safari purges any cookies from iframes when the
    // iframe domain doesn't match the parent domain, so we can't use the secure init cookie.
    ...(sessionUuid && { [CBPAY_GUEST_SESSION_UUID_HEADER]: sessionUuid }),
  };
};

/**
 * This is a `fetch` instrumented with the default `@cbhq/data-layer` logic, like automatic retries. It will also:
 * - Resolve to a `Response` (like the regular `fetch`) if `res.ok` is `true`
 * - Resolve to an `ApiError` if `res.ok` is `false`
 * - Resolve to a `NetworkError` if the `fetch` promise is rejected
 */
const instrumentedFetch = createFetch({
  ...defaultFetchConfig,
  setHeaders: {
    headers: () => getCommonHeaders(),
  },
  logger: getCustomLogger('instrumented fetch'),
  retryUnauthorized: {
    handleUnauthorized: async () => {
      // Don't try to refresh the token if we're already on the guest checkout flow
      if (window.location.pathname.startsWith('/buy/guest/')) return false;

      // USM's '/login' takes care of cleaning up the old session
      // and generating the new session with the correct TTL and scopes.
      // /v2/signin lets us control what route we're redirected back to.
      void flushQueue().then(() => {
        window.location.assign(getRouteWithParams('V2Signin'));
      });
      return false;
    },
  },
});

export async function fetchWithCommonHeaders(url: string, init?: CbDataRequestInit) {
  const res = await instrumentedFetch(url, {
    ...init,
    metricTags: { ...init?.metricTags, fetch_url: url.split('?')[0] },
  });

  if (res instanceof Error) throw res;
  return res;
}
